/**
 * Mock API Service for Therapist Contracts Module
 * Simulates REST endpoints with latency and success/failure cases
 */

import {
  Contract,
  Session,
  Note,
  ClaimData,
  AuditEntry,
  EVVRecord,
  CPTCode,
  ICDCode,
  DemoControls,
} from '@/types/contracts'

// Demo controls state
let demoControls: DemoControls = {
  mockLatency: 500,
  geolocationEnabled: true,
  aiGeneratorEnabled: true,
  simulateFailures: false,
  fastForwardTime: false,
}

// In-memory data store
let contractsStore: Contract[] = []
let auditTrailStore: AuditEntry[] = []

// CPT Codes database
const cptCodesDatabase: CPTCode[] = [
  { code: '90834', description: 'Psychotherapy, 45 minutes', duration: 45 },
  { code: '90837', description: 'Psychotherapy, 60 minutes', duration: 60 },
  { code: '97110', description: 'Therapeutic exercises', duration: 30 },
  { code: '97530', description: 'Therapeutic activities', duration: 30 },
  { code: '92507', description: 'Speech/hearing therapy', duration: 30 },
  { code: '96105', description: 'Assessment of aphasia', duration: 60 },
]

// ICD Codes database
const icdCodesDatabase: ICDCode[] = [
  { code: 'F41.1', description: 'Generalized anxiety disorder' },
  {
    code: 'F32.9',
    description: 'Major depressive disorder, single episode, unspecified',
  },
  {
    code: 'F43.10',
    description: 'Post-traumatic stress disorder, unspecified',
  },
  { code: 'M25.561', description: 'Pain in right knee' },
  {
    code: 'G81.91',
    description: 'Hemiplegia, unspecified affecting right dominant side',
  },
  { code: 'R47.01', description: 'Aphasia' },
]

/**
 * Initialize mock data
 */
export function initializeMockData(): void {
  contractsStore = [
    {
      id: 'c1',
      patient: {
        id: 'p1',
        name: 'John Doe',
        dob: '1986-02-20',
        mrn: 'MRN-001',
      },
      therapist: {
        id: 't1',
        name: 'Dr. Sarah Therapist',
        credentials: 'PT, DPT',
        npi: '1234567890',
      },
      title: '5-session CBT program',
      startDate: '2025-09-28',
      plannedSessions: 5,
      completedSessions: 2,
      status: 'active',
      diagnosis: 'Generalized anxiety disorder (F41.1)',
      treatmentGoals: [
        'Reduce anxiety symptoms by 50%',
        'Improve sleep quality',
        'Develop coping strategies for stress management',
      ],
      initialEvaluation: {
        text: 'Patient presents with symptoms of generalized anxiety disorder. GAD-7 score: 14 (moderate anxiety), PHQ-9 score: 8 (mild depression). Patient reports difficulty sleeping, constant worry, and muscle tension. Good insight and motivation for treatment.',
        scores: { GAD7: 14, PHQ9: 8 },
        date: '2025-09-27',
      },
      sessions: [
        {
          id: 's1',
          contractId: 'c1',
          scheduledAt: '2025-09-28T10:00:00.000Z',
          status: 'completed',
          locationType: 'in-person',
          evv: {
            checkInAt: '2025-09-28T09:55:00.000Z',
            checkOutAt: '2025-09-28T10:45:00.000Z',
            lat: 28.6139,
            lng: 77.209,
            checkInLocation: {
              lat: 28.6139,
              lng: 77.209,
              address: '123 Therapy Center, New Delhi, India',
            },
            checkOutLocation: {
              lat: 28.6139,
              lng: 77.209,
              address: '123 Therapy Center, New Delhi, India',
            },
          },
          notes: [
            {
              id: 'n1',
              sessionId: 's1',
              type: 'SOAP',
              text: `**Subjective:**
Patient reports reduced anxiety this week after initial evaluation. Sleep quality improved slightly. Reports practicing deep breathing exercises. Continues to experience worry about work performance.

**Objective:**
Patient alert and oriented x3. Good eye contact maintained. Speech clear and coherent. Appears less tense than initial evaluation. Engaged well in session activities.

**Assessment:**
Patient showing early signs of progress with anxiety management techniques. GAD-7 informal assessment indicates slight improvement. Patient demonstrates good understanding of CBT concepts introduced. Prognosis good with continued treatment.

**Plan:**
Continue weekly CBT sessions. Introduce cognitive restructuring techniques next session. Patient to practice thought records daily. Reassess GAD-7 and PHQ-9 at session 5. Follow up on sleep hygiene recommendations.`,
              cptCodes: ['90834'],
              icdCodes: ['F41.1'],
              patientVisible: true,
              status: 'finalized',
              createdAt: '2025-09-28T11:00:00.000Z',
              updatedAt: '2025-09-28T11:00:00.000Z',
              finalizedAt: '2025-09-28T11:30:00.000Z',
              finalizedBy: 'Dr. Sarah Therapist',
            },
          ],
          durationMinutes: 60,
          slotDurationMinutes: 60,
          claimable: true,
          paymentInfo: {
            paymentType: 'insurance',
            totalAmount: 200,
            insurancePaidAmount: 160,
            coPayAmount: 40,
            patientOwedAmount: 0,
            paymentStatus: 'in-progress',
            claimStatus: 'payer_processing',
            claimSubmittedDate: '2025-09-28T12:00:00.000Z',
          },
        },
        {
          id: 's2',
          contractId: 'c1',
          scheduledAt: '2025-10-05T10:00:00.000Z',
          status: 'clocked-out',
          locationType: 'in-person',
          evv: {
            checkInAt: '2025-10-05T09:58:00.000Z',
            checkOutAt: '2025-10-05T10:48:00.000Z',
            lat: 28.6139,
            lng: 77.209,
          },
          notes: [
            {
              id: 'n2',
              sessionId: 's2',
              type: 'SOAP',
              text: `**Subjective:**
Patient reports continued improvement in anxiety symptoms. Sleep quality has improved significantly. Successfully implemented thought record techniques. Still experiences occasional work-related stress.

**Objective:**
Patient appears more relaxed and confident. Good engagement in cognitive restructuring exercises. Demonstrated improved understanding of CBT principles.

**Assessment:**
Significant progress noted. Patient showing excellent response to treatment. GAD-7 informal assessment shows continued improvement. Good compliance with homework assignments.

**Plan:**
Continue weekly sessions. Introduce exposure therapy techniques. Patient to continue thought records and add relaxation exercises.`,
              cptCodes: ['90834'],
              icdCodes: ['F41.1'],
              patientVisible: true,
              status: 'finalized',
              createdAt: '2025-10-05T11:00:00.000Z',
              updatedAt: '2025-10-05T11:00:00.000Z',
              finalizedAt: '2025-10-05T11:30:00.000Z',
              finalizedBy: 'Dr. Sarah Therapist',
            },
          ],
          durationMinutes: 60,
          slotDurationMinutes: 60,
          claimable: true,
          paymentInfo: {
            paymentType: 'insurance',
            totalAmount: 200,
            insurancePaidAmount: 0,
            coPayAmount: 0,
            patientOwedAmount: 0,
            paymentStatus: 'in-progress',
            claimStatus: 'pending_sub',
            claimSubmittedDate: '2025-10-05T12:00:00.000Z',
          },
        },
        {
          id: 's2b',
          contractId: 'c1',
          scheduledAt: '2025-10-08T14:00:00.000Z',
          status: 'clocked-out',
          locationType: 'in-person',
          evv: {
            checkInAt: '2025-10-08T13:58:00.000Z',
            checkOutAt: '2025-10-08T14:53:00.000Z',
            lat: 28.6139,
            lng: 77.209,
          },
          notes: [
            {
              id: 'n2b',
              sessionId: 's2b',
              type: 'SOAP',
              text: `**Subjective:**
Patient reports feeling much better. Sleep has normalized. Successfully managing work stress with the techniques learned. Feels confident about progress.

**Objective:**
Patient appears calm and confident. Excellent engagement throughout the session. Demonstrated mastery of CBT techniques. Minimal anxiety indicators observed.

**Assessment:**
Excellent progress. Patient has achieved significant improvement in anxiety management. Ready to discuss maintenance strategies. Prognosis excellent.

**Plan:**
Continue weekly sessions. Focus on relapse prevention strategies. Patient to maintain thought records. Reassess treatment goals next session.`,
              cptCodes: ['90834'],
              icdCodes: ['F41.1'],
              patientVisible: true,
              status: 'finalized',
              createdAt: '2025-10-08T15:00:00.000Z',
              updatedAt: '2025-10-08T15:00:00.000Z',
              finalizedAt: '2025-10-08T15:15:00.000Z',
              finalizedBy: 'Dr. Sarah Therapist',
            },
          ],
          durationMinutes: 55,
          slotDurationMinutes: 60,
          claimable: true,
          paymentInfo: {
            paymentType: 'self-pay',
            totalAmount: 180,
            patientOwedAmount: 180,
            paymentStatus: 'pending',
            claimStatus: 'not-applicable',
          },
        },
        {
          id: 's3',
          contractId: 'c1',
          scheduledAt: '2025-10-12T10:00:00.000Z',
          status: 'scheduled',
          locationType: 'in-person',
          evv: {},
          notes: [],
          durationMinutes: 60,
          slotDurationMinutes: 60,
          claimable: false,
          paymentInfo: {
            paymentType: 'self-pay',
            totalAmount: 150,
            patientOwedAmount: 150,
            paymentStatus: 'pending',
            claimStatus: 'not-applicable',
          },
        },
        {
          id: 's4',
          contractId: 'c1',
          scheduledAt: '2025-10-19T10:00:00.000Z',
          status: 'scheduled',
          locationType: 'in-person',
          evv: {},
          notes: [],
          durationMinutes: 60,
          slotDurationMinutes: 60,
          claimable: false,
          paymentInfo: {
            paymentType: 'insurance',
            totalAmount: 200,
            insurancePaidAmount: 180,
            coPayAmount: 20,
            patientOwedAmount: 20,
            paymentStatus: 'invoice-sent',
            claimStatus: 'approved',
            invoiceId: 'INV-2025-001',
            invoiceDate: '2025-10-20T10:00:00.000Z',
            claimSubmittedDate: '2025-10-19T12:00:00.000Z',
            claimProcessedDate: '2025-10-20T09:00:00.000Z',
          },
        },
        {
          id: 's5',
          contractId: 'c1',
          scheduledAt: '2025-10-26T10:00:00.000Z',
          status: 'scheduled',
          locationType: 'in-person',
          evv: {},
          notes: [],
          durationMinutes: 60,
          slotDurationMinutes: 60,
          claimable: false,
          paymentInfo: {
            paymentType: 'self-pay',
            totalAmount: 150,
            patientOwedAmount: 0,
            paymentStatus: 'fully-paid',
            claimStatus: 'not-applicable',
            invoiceId: 'INV-2025-002',
            invoiceDate: '2025-10-26T10:00:00.000Z',
            paymentDate: '2025-10-27T14:30:00.000Z',
          },
        },
      ],
    },
    {
      id: 'c2',
      patient: {
        id: 'p2',
        name: 'Sarah Johnson',
        dob: '1992-07-15',
        mrn: 'MRN-002',
      },
      therapist: {
        id: 't1',
        name: 'Dr. Sarah Therapist',
        credentials: 'PT, DPT',
        npi: '1234567890',
      },
      title: '3-session knee rehabilitation',
      startDate: '2025-09-20',
      plannedSessions: 3,
      completedSessions: 1,
      status: 'active',
      diagnosis: 'Pain in right knee (M25.561)',
      treatmentGoals: [
        'Restore full range of motion',
        'Increase quadriceps strength to 80% of left side',
        'Return to normal walking pattern',
      ],
      initialEvaluation: {
        text: 'Patient s/p right knee arthroscopy 4 weeks ago. Limited ROM: flexion 95°, extension -5°. Quad strength 3/5. Mild effusion present. Gait antalgic with decreased stance phase on right.',
        scores: { FlexionROM: 95, ExtensionROM: -5, QuadStrength: 3 },
        date: '2025-09-19',
      },
      sessions: [
        {
          id: 's6',
          contractId: 'c2',
          scheduledAt: '2025-09-21T14:00:00.000Z',
          status: 'completed',
          locationType: 'in-person',
          evv: {
            checkInAt: '2025-09-21T13:58:00.000Z',
            checkOutAt: '2025-09-21T14:28:00.000Z',
            lat: 28.6139,
            lng: 77.209,
          },
          notes: [
            {
              id: 'n3',
              sessionId: 's6',
              type: 'SOAP',
              text: 'S: Patient reports decreased pain with daily activities.\nO: ROM improved - flexion 105°. Performed therapeutic exercises.\nA: Good progress with ROM goals.\nP: Continue current exercise program, add resistance training.',
              cptCodes: ['97110'],
              icdCodes: ['M25.561'],
              patientVisible: true,
              status: 'finalized',
              createdAt: '2025-09-21T14:30:00.000Z',
              updatedAt: '2025-09-21T14:30:00.000Z',
              finalizedAt: '2025-09-21T14:45:00.000Z',
              finalizedBy: 'Dr. Sarah Therapist',
            },
          ],
          durationMinutes: 30,
          slotDurationMinutes: 30,
          claimable: true,
          paymentInfo: {
            paymentType: 'insurance',
            totalAmount: 180,
            insurancePaidAmount: 150,
            coPayAmount: 30,
            patientOwedAmount: 0,
            paymentStatus: 'fully-paid',
            claimStatus: 'paid',
            invoiceId: 'INV-2025-003',
            invoiceDate: '2025-09-22T10:00:00.000Z',
            paymentDate: '2025-09-22T10:00:00.000Z',
            claimSubmittedDate: '2025-09-21T15:00:00.000Z',
            claimProcessedDate: '2025-09-23T11:00:00.000Z',
          },
        },
        {
          id: 's7',
          contractId: 'c2',
          scheduledAt: '2025-10-08T14:00:00.000Z',
          status: 'scheduled',
          locationType: 'in-person',
          evv: {},
          notes: [],
          durationMinutes: 30,
          slotDurationMinutes: 30,
          claimable: false,
          paymentInfo: {
            paymentType: 'insurance',
            totalAmount: 180,
            insurancePaidAmount: 0,
            coPayAmount: 0,
            patientOwedAmount: 0,
            paymentStatus: 'in-progress',
            claimStatus: 'denied',
            claimSubmittedDate: '2025-10-08T15:00:00.000Z',
            claimProcessedDate: '2025-10-09T09:00:00.000Z',
            denialReason: 'Prior authorization required',
          },
        },
        {
          id: 's8',
          contractId: 'c2',
          scheduledAt: '2025-10-15T14:00:00.000Z',
          status: 'scheduled',
          locationType: 'in-person',
          evv: {},
          notes: [],
          durationMinutes: 30,
          slotDurationMinutes: 30,
          claimable: false,
          paymentInfo: {
            paymentType: 'insurance',
            totalAmount: 180,
            insurancePaidAmount: 0,
            coPayAmount: 30,
            patientOwedAmount: 30,
            paymentStatus: 'pending',
            claimStatus: 'pending',
          },
        },
      ],
    },
    // Pending Contract - waiting for e-signatures
    {
      id: 'c3',
      patient: {
        id: 'p3',
        name: 'Michael Chen',
        dob: '1988-11-03',
        mrn: 'MRN-003',
      },
      therapist: {
        id: 't1',
        name: 'Dr. Sarah Therapist',
        credentials: 'PT, DPT',
        npi: '1234567890',
      },
      title: '8-session speech therapy program',
      startDate: '2025-10-15',
      plannedSessions: 8,
      completedSessions: 0,
      status: 'pending',
      diagnosis: 'Aphasia (R47.01)',
      treatmentGoals: [
        'Improve speech clarity by 70%',
        'Enhance communication skills',
        'Restore functional language abilities',
      ],
      patientSigned: true,
      therapistSigned: false,
      initialEvaluation: {
        text: 'Patient presents with expressive aphasia following stroke 3 months ago. Difficulty with word finding and sentence construction. Good comprehension skills. Motivated for therapy.',
        scores: { AphasiaScore: 45, ComprehensionScore: 85 },
        date: '2025-10-14',
      },
      sessions: [
        {
          id: 's-pending-1',
          contractId: 'c3',
          scheduledAt: '2025-10-15T14:00:00.000Z',
          status: 'scheduled',
          locationType: 'in-person',
          evv: {},
          notes: [],
          durationMinutes: 45,
          slotDurationMinutes: 45,
          claimable: false,
          paymentInfo: {
            paymentType: 'self-pay',
            totalAmount: 175,
            patientOwedAmount: 175,
            paymentStatus: 'pending',
            claimStatus: 'not-applicable',
          },
        },
        {
          id: 's-pending-2',
          contractId: 'c3',
          scheduledAt: '2025-10-17T14:00:00.000Z',
          status: 'scheduled',
          locationType: 'in-person',
          evv: {},
          notes: [],
          durationMinutes: 45,
          slotDurationMinutes: 45,
          claimable: false,
          paymentInfo: {
            paymentType: 'self-pay',
            totalAmount: 175,
            patientOwedAmount: 175,
            paymentStatus: 'pending',
            claimStatus: 'not-applicable',
          },
        },
        {
          id: 's-pending-3',
          contractId: 'c3',
          scheduledAt: '2025-10-22T14:00:00.000Z',
          status: 'scheduled',
          locationType: 'home',
          evv: {},
          notes: [],
          durationMinutes: 45,
          slotDurationMinutes: 45,
          claimable: false,
        },
        {
          id: 's-pending-4',
          contractId: 'c3',
          scheduledAt: '2025-10-24T14:00:00.000Z',
          status: 'scheduled',
          locationType: 'in-person',
          evv: {},
          notes: [],
          durationMinutes: 45,
          slotDurationMinutes: 45,
          claimable: false,
        },
        {
          id: 's-pending-5',
          contractId: 'c3',
          scheduledAt: '2025-10-29T14:00:00.000Z',
          status: 'scheduled',
          locationType: 'telehealth',
          evv: {},
          notes: [],
          durationMinutes: 45,
          slotDurationMinutes: 45,
          claimable: false,
        },
        {
          id: 's-pending-6',
          contractId: 'c3',
          scheduledAt: '2025-10-31T14:00:00.000Z',
          status: 'scheduled',
          locationType: 'in-person',
          evv: {},
          notes: [],
          durationMinutes: 45,
          slotDurationMinutes: 45,
          claimable: false,
        },
        {
          id: 's-pending-7',
          contractId: 'c3',
          scheduledAt: '2025-11-05T14:00:00.000Z',
          status: 'scheduled',
          locationType: 'in-person',
          evv: {},
          notes: [],
          durationMinutes: 45,
          slotDurationMinutes: 45,
          claimable: false,
        },
        {
          id: 's-pending-8',
          contractId: 'c3',
          scheduledAt: '2025-11-07T14:00:00.000Z',
          status: 'scheduled',
          locationType: 'home',
          evv: {},
          notes: [],
          durationMinutes: 45,
          slotDurationMinutes: 45,
          claimable: false,
        },
      ],
    },
    // Another Pending Contract - therapist hasn't signed yet
    {
      id: 'c4',
      patient: {
        id: 'p4',
        name: 'Emily Rodriguez',
        dob: '1995-04-22',
        mrn: 'MRN-004',
      },
      therapist: {
        id: 't1',
        name: 'Dr. Sarah Therapist',
        credentials: 'PT, DPT',
        npi: '1234567890',
      },
      title: '6-session physical therapy',
      startDate: '2025-10-20',
      plannedSessions: 6,
      completedSessions: 0,
      status: 'pending',
      diagnosis:
        'Hemiplegia, unspecified affecting right dominant side (G81.91)',
      treatmentGoals: [
        'Improve right arm mobility',
        'Increase muscle strength',
        'Restore functional independence',
      ],
      patientSigned: true,
      therapistSigned: false,
      initialEvaluation: {
        text: 'Patient with right hemiplegia post-stroke 2 months ago. Limited ROM in right upper extremity. Good cognitive function and motivation.',
        scores: { RightArmROM: 30, MuscleStrength: 2 },
        date: '2025-10-19',
      },
      sessions: [
        {
          id: 's-pending-9',
          contractId: 'c4',
          scheduledAt: '2025-10-20T10:00:00.000Z',
          status: 'scheduled',
          locationType: 'in-person',
          evv: {},
          notes: [],
          durationMinutes: 60,
          slotDurationMinutes: 60,
          claimable: false,
        },
        {
          id: 's-pending-10',
          contractId: 'c4',
          scheduledAt: '2025-10-22T10:00:00.000Z',
          status: 'scheduled',
          locationType: 'in-person',
          evv: {},
          notes: [],
          durationMinutes: 60,
          slotDurationMinutes: 60,
          claimable: false,
        },
        {
          id: 's-pending-11',
          contractId: 'c4',
          scheduledAt: '2025-10-27T10:00:00.000Z',
          status: 'scheduled',
          locationType: 'home',
          evv: {},
          notes: [],
          durationMinutes: 60,
          slotDurationMinutes: 60,
          claimable: false,
        },
        {
          id: 's-pending-12',
          contractId: 'c4',
          scheduledAt: '2025-10-29T10:00:00.000Z',
          status: 'scheduled',
          locationType: 'in-person',
          evv: {},
          notes: [],
          durationMinutes: 60,
          slotDurationMinutes: 60,
          claimable: false,
        },
        {
          id: 's-pending-13',
          contractId: 'c4',
          scheduledAt: '2025-11-03T10:00:00.000Z',
          status: 'scheduled',
          locationType: 'in-person',
          evv: {},
          notes: [],
          durationMinutes: 60,
          slotDurationMinutes: 60,
          claimable: false,
        },
        {
          id: 's-pending-14',
          contractId: 'c4',
          scheduledAt: '2025-11-05T10:00:00.000Z',
          status: 'scheduled',
          locationType: 'telehealth',
          evv: {},
          notes: [],
          durationMinutes: 60,
          slotDurationMinutes: 60,
          claimable: false,
        },
      ],
    },
    // // Another Pending Contract - therapist hasn't signed yet
    // {
    //   id: 'c4',
    //   patient: {
    //     id: 'p4',
    //     name: 'Emily Rodriguez',
    //     dob: '1995-04-22',
    //     mrn: 'MRN-004',
    //   },
    //   therapist: {
    //     id: 't1',
    //     name: 'Dr. Anil Singh',
    //     credentials: 'PT, DPT',
    //     npi: '1234567890',
    //   },
    //   title: '6-session physical therapy',
    //   startDate: '2025-10-20',
    //   plannedSessions: 6,
    //   completedSessions: 0,
    //   status: 'pending',
    //   diagnosis:
    //     'Hemiplegia, unspecified affecting right dominant side (G81.91)',
    //   treatmentGoals: [
    //     'Improve right arm mobility',
    //     'Increase muscle strength',
    //     'Restore functional independence',
    //   ],
    //   patientSigned: true,
    //   therapistSigned: false,
    //   initialEvaluation: {
    //     text: 'Patient with right hemiplegia post-stroke 2 months ago. Limited ROM in right upper extremity. Good cognitive function and motivation.',
    //     scores: { RightArmROM: 30, MuscleStrength: 2 },
    //     date: '2025-10-19',
    //   },
    //   sessions: [],
    // },
    // Completed Contract - all sessions finished
    {
      id: 'c5',
      patient: {
        id: 'p5',
        name: 'David Wilson',
        dob: '1979-09-08',
        mrn: 'MRN-005',
      },
      therapist: {
        id: 't1',
        name: 'Dr. Sarah Therapist',
        credentials: 'PT, DPT',
        npi: '1234567890',
      },
      title: '4-session depression treatment',
      startDate: '2025-08-15',
      endDate: '2025-09-12',
      plannedSessions: 4,
      completedSessions: 4,
      status: 'completed',
      diagnosis:
        'Major depressive disorder, single episode, unspecified (F32.9)',
      treatmentGoals: [
        'Reduce depression symptoms by 60%',
        'Improve daily functioning',
        'Develop coping strategies',
      ],
      patientSigned: true,
      therapistSigned: true,
      signedAt: '2025-08-14T10:00:00.000Z',
      initialEvaluation: {
        text: 'Patient presents with symptoms of major depression. PHQ-9 score: 16 (moderate-severe depression). Reports difficulty with daily activities and social interactions.',
        scores: { PHQ9: 16, GAD7: 12 },
        date: '2025-08-14',
      },
      sessions: [
        {
          id: 's9',
          contractId: 'c5',
          scheduledAt: '2025-08-15T11:00:00.000Z',
          status: 'completed',
          locationType: 'in-person',
          evv: {
            checkInAt: '2025-08-15T10:58:00.000Z',
            checkOutAt: '2025-08-15T11:58:00.000Z',
          },
          notes: [
            {
              id: 'n4',
              sessionId: 's9',
              type: 'SOAP',
              text: 'Initial therapy session. Patient engaged well. Discussed treatment goals and coping strategies.',
              cptCodes: ['90834'],
              icdCodes: ['F32.9'],
              patientVisible: true,
              status: 'finalized',
              createdAt: '2025-08-15T12:00:00.000Z',
              updatedAt: '2025-08-15T12:00:00.000Z',
              finalizedAt: '2025-08-15T12:30:00.000Z',
              finalizedBy: 'Dr. Sarah Therapist',
            },
          ],
          durationMinutes: 60,
          slotDurationMinutes: 60,
          claimable: true,
          paymentInfo: {
            paymentType: 'self-pay',
            totalAmount: 180,
            patientOwedAmount: 0,
            paymentStatus: 'fully-paid',
            claimStatus: 'not-applicable',
            invoiceId: 'INV-2025-101',
            invoiceDate: '2025-08-15T13:00:00.000Z',
            paymentDate: '2025-08-16T09:30:00.000Z',
          },
        },
        {
          id: 's10',
          contractId: 'c5',
          scheduledAt: '2025-08-22T11:00:00.000Z',
          status: 'completed',
          locationType: 'in-person',
          evv: {
            checkInAt: '2025-08-22T10:59:00.000Z',
            checkOutAt: '2025-08-22T11:59:00.000Z',
          },
          notes: [
            {
              id: 'n5',
              sessionId: 's10',
              type: 'SOAP',
              text: 'Continued progress. Patient reports improved mood. Introduced cognitive behavioral techniques.',
              cptCodes: ['90834'],
              icdCodes: ['F32.9'],
              patientVisible: true,
              status: 'finalized',
              createdAt: '2025-08-22T12:00:00.000Z',
              updatedAt: '2025-08-22T12:00:00.000Z',
              finalizedAt: '2025-08-22T12:30:00.000Z',
              finalizedBy: 'Dr. Sarah Therapist',
            },
          ],
          durationMinutes: 60,
          slotDurationMinutes: 60,
          claimable: true,
          paymentInfo: {
            paymentType: 'insurance',
            totalAmount: 200,
            insurancePaidAmount: 170,
            coPayAmount: 30,
            patientOwedAmount: 0,
            paymentStatus: 'fully-paid',
            claimStatus: 'approved',
            invoiceId: 'INV-2025-102',
            invoiceDate: '2025-08-22T14:00:00.000Z',
            paymentDate: '2025-08-23T11:00:00.000Z',
            claimSubmittedDate: '2025-08-22T13:00:00.000Z',
            claimProcessedDate: '2025-08-24T10:00:00.000Z',
          },
        },
        {
          id: 's11',
          contractId: 'c5',
          scheduledAt: '2025-08-29T11:00:00.000Z',
          status: 'completed',
          locationType: 'in-person',
          evv: {
            checkInAt: '2025-08-29T10:57:00.000Z',
            checkOutAt: '2025-08-29T11:57:00.000Z',
          },
          notes: [
            {
              id: 'n6',
              sessionId: 's11',
              type: 'SOAP',
              text: 'Significant improvement noted. PHQ-9 score decreased to 8. Patient implementing coping strategies effectively.',
              cptCodes: ['90834'],
              icdCodes: ['F32.9'],
              patientVisible: true,
              status: 'finalized',
              createdAt: '2025-08-29T12:00:00.000Z',
              updatedAt: '2025-08-29T12:00:00.000Z',
              finalizedAt: '2025-08-29T12:30:00.000Z',
              finalizedBy: 'Dr. Sarah Therapist',
            },
          ],
          durationMinutes: 60,
          slotDurationMinutes: 60,
          claimable: true,
          paymentInfo: {
            paymentType: 'insurance',
            totalAmount: 200,
            insurancePaidAmount: 165,
            coPayAmount: 35,
            patientOwedAmount: 0,
            paymentStatus: 'fully-paid',
            claimStatus: 'approved',
            invoiceId: 'INV-2025-103',
            invoiceDate: '2025-08-30T10:00:00.000Z',
            paymentDate: '2025-08-30T10:00:00.000Z',
            claimSubmittedDate: '2025-08-29T13:00:00.000Z',
            claimProcessedDate: '2025-08-30T09:00:00.000Z',
          },
        },
        {
          id: 's12',
          contractId: 'c5',
          scheduledAt: '2025-09-12T11:00:00.000Z',
          status: 'completed',
          locationType: 'in-person',
          evv: {
            checkInAt: '2025-09-12T10:58:00.000Z',
            checkOutAt: '2025-09-12T11:58:00.000Z',
          },
          notes: [
            {
              id: 'n7',
              sessionId: 's12',
              type: 'Discharge',
              text: 'Final session completed. Patient achieved all treatment goals. PHQ-9 score: 4 (minimal depression). Discharged with maintenance plan.',
              cptCodes: ['90834'],
              icdCodes: ['F32.9'],
              patientVisible: true,
              status: 'finalized',
              createdAt: '2025-09-12T12:00:00.000Z',
              updatedAt: '2025-09-12T12:00:00.000Z',
              finalizedAt: '2025-09-12T12:30:00.000Z',
              finalizedBy: 'Dr. Sarah Therapist',
            },
          ],
          durationMinutes: 60,
          slotDurationMinutes: 60,
          claimable: true,
          paymentInfo: {
            paymentType: 'insurance',
            totalAmount: 200,
            insurancePaidAmount: 175,
            coPayAmount: 25,
            patientOwedAmount: 25,
            paymentStatus: 'pending',
            claimStatus: 'approved',
            // invoiceId: 'INV-2025-104',
            // invoiceDate: '2025-09-12T13:00:00.000Z',
            // paymentDate: '2025-09-13T15:00:00.000Z',
            claimSubmittedDate: '2025-09-12T14:00:00.000Z',
            claimProcessedDate: '2025-09-14T08:00:00.000Z',
          },
        },
      ],
    },
  ]

  // Initialize audit trail
  auditTrailStore = [
    {
      id: 'a1',
      timestamp: '2025-09-28T09:55:00.000Z',
      action: 'EVV_CHECK_IN',
      user: 'Dr. Sarah Therapist',
      details: { sessionId: 's1', location: 'Therapy Center' },
      entityType: 'session',
      entityId: 's1',
    },
    {
      id: 'a2',
      timestamp: '2025-09-28T10:45:00.000Z',
      action: 'EVV_CHECK_OUT',
      user: 'Dr. Sarah Therapist',
      details: { sessionId: 's1', duration: 50 },
      entityType: 'session',
      entityId: 's1',
    },
    {
      id: 'a3',
      timestamp: '2025-09-28T11:30:00.000Z',
      action: 'NOTE_FINALIZED',
      user: 'Dr. Sarah Therapist',
      details: { noteId: 'n1', type: 'SOAP' },
      entityType: 'note',
      entityId: 'n1',
    },
  ]
}

// Initialize on load
initializeMockData()

/**
 * Simulate network latency
 */
const delay = (ms?: number): Promise<void> => {
  const latency = ms || demoControls.mockLatency
  return new Promise(resolve => setTimeout(resolve, latency))
}

/**
 * Check if should simulate failure
 */
const shouldFail = (): boolean => {
  return demoControls.simulateFailures && Math.random() < 0.1
}

/**
 * Add audit entry
 */
const addAuditEntry = (entry: Omit<AuditEntry, 'id' | 'timestamp'>): void => {
  const auditEntry: AuditEntry = {
    id: `a${Date.now()}`,
    timestamp: new Date().toISOString(),
    ...entry,
  }
  auditTrailStore.unshift(auditEntry)
}

// ==================== API Methods ====================

/**
 * Get demo controls
 */
export const getDemoControls = (): DemoControls => {
  return { ...demoControls }
}

/**
 * Update demo controls
 */
export const updateDemoControls = async (
  controls: Partial<DemoControls>
): Promise<DemoControls> => {
  demoControls = { ...demoControls, ...controls }
  return { ...demoControls }
}

/**
 * Get all contracts
 */
export const getContracts = async (): Promise<Contract[]> => {
  await delay()
  if (shouldFail()) {
    throw new Error('Failed to fetch contracts')
  }
  return [...contractsStore]
}

/**
 * Get contract by ID
 */
export const getContract = async (id: string): Promise<Contract | null> => {
  await delay()
  if (shouldFail()) {
    throw new Error('Failed to fetch contract')
  }
  const contract = contractsStore.find(c => c.id === id)
  return contract ? { ...contract } : null
}

/**
 * Create new contract
 */
export const createContract = async (
  contract: Omit<Contract, 'id' | 'completedSessions'>
): Promise<Contract> => {
  await delay()
  if (shouldFail()) {
    throw new Error('Failed to create contract')
  }

  const newContract: Contract = {
    ...contract,
    id: `c${Date.now()}`,
    completedSessions: 0,
  }

  contractsStore.push(newContract)

  addAuditEntry({
    action: 'CONTRACT_CREATED',
    user: contract.therapist.name,
    details: { contractId: newContract.id, patient: contract.patient.name },
    entityType: 'contract',
    entityId: newContract.id,
  })

  return { ...newContract }
}

/**
 * Update contract
 */
export const updateContract = async (
  id: string,
  updates: Partial<Contract>
): Promise<Contract> => {
  await delay()
  if (shouldFail()) {
    throw new Error('Failed to update contract')
  }

  const index = contractsStore.findIndex(c => c.id === id)
  if (index === -1) {
    throw new Error('Contract not found')
  }

  contractsStore[index] = { ...contractsStore[index], ...updates }

  addAuditEntry({
    action: 'CONTRACT_UPDATED',
    user: 'Current User',
    details: { contractId: id, changes: Object.keys(updates) },
    entityType: 'contract',
    entityId: id,
  })

  return { ...contractsStore[index] }
}

/**
 * EVV Check-in
 */
export const evvCheckIn = async (
  sessionId: string,
  location?: { lat: number; lng: number; address?: string }
): Promise<Session> => {
  await delay()
  if (shouldFail()) {
    throw new Error('Failed to check in')
  }

  const contract = contractsStore.find(c =>
    c.sessions.some(s => s.id === sessionId)
  )
  if (!contract) {
    throw new Error('Session not found')
  }

  const session = contract.sessions.find(s => s.id === sessionId)
  if (!session) {
    throw new Error('Session not found')
  }

  if (session.evv.checkInAt) {
    throw new Error('Already checked in')
  }

  const checkInTime = new Date().toISOString()
  session.evv.checkInAt = checkInTime
  session.status = 'in-progress'

  if (location) {
    session.evv.lat = location.lat
    session.evv.lng = location.lng
    session.evv.checkInLocation = location
  } else if (!demoControls.geolocationEnabled) {
    session.evv.manualOverride = true
    session.evv.overrideReason = 'Geolocation unavailable'
  }

  addAuditEntry({
    action: 'EVV_CHECK_IN',
    user: contract.therapist.name,
    details: {
      sessionId,
      location: location?.address || 'Unknown',
      time: checkInTime,
    },
    entityType: 'session',
    entityId: sessionId,
  })

  return { ...session }
}

/**
 * EVV Check-out
 */
export const evvCheckOut = async (
  sessionId: string,
  location?: { lat: number; lng: number; address?: string }
): Promise<Session> => {
  await delay()
  if (shouldFail()) {
    throw new Error('Failed to check out')
  }

  const contract = contractsStore.find(c =>
    c.sessions.some(s => s.id === sessionId)
  )
  if (!contract) {
    throw new Error('Session not found')
  }

  const session = contract.sessions.find(s => s.id === sessionId)
  if (!session) {
    throw new Error('Session not found')
  }

  if (!session.evv.checkInAt) {
    throw new Error('Must check in before checking out')
  }

  if (session.evv.checkOutAt) {
    throw new Error('Already checked out')
  }

  const checkOutTime = new Date().toISOString()
  session.evv.checkOutAt = checkOutTime
  session.status = 'clocked-out'

  if (location) {
    session.evv.checkOutLocation = location
  }

  // Calculate duration
  const checkIn = new Date(session.evv.checkInAt).getTime()
  const checkOut = new Date(checkOutTime).getTime()
  session.durationMinutes = Math.round((checkOut - checkIn) / 60000)

  addAuditEntry({
    action: 'EVV_CHECK_OUT',
    user: contract.therapist.name,
    details: {
      sessionId,
      location: location?.address || 'Unknown',
      duration: session.durationMinutes,
      time: checkOutTime,
    },
    entityType: 'session',
    entityId: sessionId,
  })

  return { ...session }
}

/**
 * Create or update note
 */
export const saveNote = async (
  sessionId: string,
  note: Omit<Note, 'id' | 'createdAt' | 'updatedAt'>
): Promise<Note> => {
  await delay()
  if (shouldFail()) {
    throw new Error('Failed to save note')
  }

  const contract = contractsStore.find(c =>
    c.sessions.some(s => s.id === sessionId)
  )
  if (!contract) {
    throw new Error('Session not found')
  }

  const session = contract.sessions.find(s => s.id === sessionId)
  if (!session) {
    throw new Error('Session not found')
  }

  const now = new Date().toISOString()
  const newNote: Note = {
    ...note,
    id: `n${Date.now()}`,
    createdAt: now,
    updatedAt: now,
  }

  session.notes.push(newNote)

  addAuditEntry({
    action: 'NOTE_CREATED',
    user: contract.therapist.name,
    details: { noteId: newNote.id, type: note.type, status: note.status },
    entityType: 'note',
    entityId: newNote.id,
  })

  return { ...newNote }
}

/**
 * Update existing note
 */
export const updateNote = async (
  sessionId: string,
  noteId: string,
  updates: Partial<Note>
): Promise<Note> => {
  await delay()
  if (shouldFail()) {
    throw new Error('Failed to update note')
  }

  const contract = contractsStore.find(c =>
    c.sessions.some(s => s.id === sessionId)
  )
  if (!contract) {
    throw new Error('Session not found')
  }

  const session = contract.sessions.find(s => s.id === sessionId)
  if (!session) {
    throw new Error('Session not found')
  }

  const noteIndex = session.notes.findIndex(n => n.id === noteId)
  if (noteIndex === -1) {
    throw new Error('Note not found')
  }

  const existingNote = session.notes[noteIndex]

  // Prevent editing finalized notes
  if (existingNote.status === 'finalized' && updates.content) {
    throw new Error('Cannot edit finalized note. Use addendum instead.')
  }

  session.notes[noteIndex] = {
    ...existingNote,
    ...updates,
    updatedAt: new Date().toISOString(),
  }

  addAuditEntry({
    action: 'NOTE_UPDATED',
    user: contract.therapist.name,
    details: { noteId, changes: Object.keys(updates) },
    entityType: 'note',
    entityId: noteId,
  })

  return { ...session.notes[noteIndex] }
}

/**
 * Finalize note
 */
export const finalizeNote = async (
  sessionId: string,
  noteId: string
): Promise<Note> => {
  await delay()
  if (shouldFail()) {
    throw new Error('Failed to finalize note')
  }

  const contract = contractsStore.find(c =>
    c.sessions.some(s => s.id === sessionId)
  )
  if (!contract) {
    throw new Error('Session not found')
  }

  const session = contract.sessions.find(s => s.id === sessionId)
  if (!session) {
    throw new Error('Session not found')
  }

  const noteIndex = session.notes.findIndex(n => n.id === noteId)
  if (noteIndex === -1) {
    throw new Error('Note not found')
  }

  const note = session.notes[noteIndex]

  // Validate EVV exists
  if (!session.evv.checkInAt || !session.evv.checkOutAt) {
    throw new Error(
      'EVV check-in and check-out required before finalizing note'
    )
  }

  // Validate CPT/ICD codes
  // if (note.cptCodes.length === 0 || note.icdCodes.length === 0) {
  //   throw new Error('CPT and ICD codes required before finalizing note')
  // }

  const now = new Date().toISOString()
  session.notes[noteIndex] = {
    ...note,
    status: 'finalized',
    finalizedAt: now,
    finalizedBy: contract.therapist.name,
    updatedAt: now,
  }

  // Mark session as claimable
  session.claimable = true

  // Update completed sessions count
  const completedCount = contract.sessions.filter(s => s.claimable).length
  contract.completedSessions = completedCount

  addAuditEntry({
    action: 'NOTE_FINALIZED',
    user: contract.therapist.name,
    details: { noteId, sessionId },
    entityType: 'note',
    entityId: noteId,
  })

  return { ...session.notes[noteIndex] }
}

/**
 * Add addendum to finalized note
 */
export const addAddendum = async (
  sessionId: string,
  noteId: string,
  addendumText: string
): Promise<Note> => {
  await delay()
  if (shouldFail()) {
    throw new Error('Failed to add addendum')
  }

  const contract = contractsStore.find(c =>
    c.sessions.some(s => s.id === sessionId)
  )
  if (!contract) {
    throw new Error('Session not found')
  }

  const session = contract.sessions.find(s => s.id === sessionId)
  if (!session) {
    throw new Error('Session not found')
  }

  const noteIndex = session.notes.findIndex(n => n.id === noteId)
  if (noteIndex === -1) {
    throw new Error('Note not found')
  }

  const note = session.notes[noteIndex]

  if (note.status !== 'finalized') {
    throw new Error('Can only add addendum to finalized notes')
  }

  const addendum = {
    id: `add${Date.now()}`,
    text: addendumText,
    createdAt: new Date().toISOString(),
    createdBy: contract.therapist.name,
  }

  session.notes[noteIndex] = {
    ...note,
    addendums: [...(note.addendums || []), addendum],
  }

  addAuditEntry({
    action: 'ADDENDUM_ADDED',
    user: contract.therapist.name,
    details: { noteId, addendumId: addendum.id },
    entityType: 'note',
    entityId: noteId,
  })

  return { ...session.notes[noteIndex] }
}

/**
 * Generate SOAP note using AI (mock)
 */
export const generateSOAPNote = async (
  sessionId: string,
  transcript?: string
): Promise<{
  text: string
  suggestedCPT: string[]
  suggestedICD: string[]
}> => {
  await delay(1500) // Longer delay to simulate AI processing

  if (!demoControls.aiGeneratorEnabled) {
    throw new Error('AI generator is disabled')
  }

  if (shouldFail()) {
    throw new Error('AI generation failed')
  }

  const contract = contractsStore.find(c =>
    c.sessions.some(s => s.id === sessionId)
  )
  if (!contract) {
    throw new Error('Session not found')
  }

  // Mock AI-generated SOAP note
  const soapText = `**Subjective:**
${transcript || 'Patient reports feeling better since last session. Sleep quality has improved. Continues to practice coping strategies discussed. Reports some challenges with work-related stress but managing better than before.'}

**Objective:**
Patient alert and oriented x3. Appropriate affect and mood. Good engagement throughout session. Demonstrated understanding of therapeutic concepts. Active participation in exercises and discussions.

**Assessment:**
Patient showing continued progress toward treatment goals. Demonstrates improved coping skills and emotional regulation. Appropriate insight into condition. Response to treatment has been positive. Prognosis remains good with continued adherence to treatment plan.

**Plan:**
Continue current treatment plan with weekly sessions. Introduce advanced coping strategies in next session. Patient to continue practicing techniques at home. Reassess progress at mid-point of treatment. Follow up on homework assignments.`

  return {
    text: soapText,
    suggestedCPT: ['90834'],
    suggestedICD: ['F41.1'],
  }
}

/**
 * Get CPT codes (with search)
 */
export const searchCPTCodes = async (query: string): Promise<CPTCode[]> => {
  await delay(300)
  const q = query.toLowerCase()
  return cptCodesDatabase.filter(
    code =>
      code.code.toLowerCase().includes(q) ||
      code.description.toLowerCase().includes(q)
  )
}

/**
 * Get ICD codes (with search)
 */
export const searchICDCodes = async (query: string): Promise<ICDCode[]> => {
  await delay(300)
  const q = query.toLowerCase()
  return icdCodesDatabase.filter(
    code =>
      code.code.toLowerCase().includes(q) ||
      code.description.toLowerCase().includes(q)
  )
}

/**
 * Generate claim data for sessions
 */
export const generateClaimData = async (
  sessionIds: string[]
): Promise<ClaimData[]> => {
  await delay()
  if (shouldFail()) {
    throw new Error('Failed to generate claim data')
  }

  const claims: ClaimData[] = []

  for (const sessionId of sessionIds) {
    const contract = contractsStore.find(c =>
      c.sessions.some(s => s.id === sessionId)
    )
    if (!contract) continue

    const session = contract.sessions.find(s => s.id === sessionId)
    if (!session || !session.claimable) continue

    const note = session.notes.find(n => n.status === 'finalized')
    if (!note) continue

    const claim: ClaimData = {
      resourceType: 'Claim',
      id: `claim-${sessionId}`,
      patient: contract.patient,
      provider: contract.therapist,
      serviceDate: session.scheduledAt.split('T')[0],
      startTime: session.evv.checkInAt || '',
      endTime: session.evv.checkOutAt || '',
      durationMinutes: session.durationMinutes || 0,
      cpt: note.cptCodes,
      icd: note.icdCodes,
      evv: session.evv,
      noteRef: note.id,
      claimStatus: 'pending',
    }

    claims.push(claim)
  }

  return claims
}

/**
 * Export claim data as JSON
 */
export const exportClaimsJSON = (claims: ClaimData[]): string => {
  return JSON.stringify(claims, null, 2)
}

/**
 * Export claim data as CSV
 */
export const exportClaimsCSV = (claims: ClaimData[]): string => {
  const headers = [
    'Claim ID',
    'Patient Name',
    'Patient DOB',
    'Provider Name',
    'Service Date',
    'Start Time',
    'End Time',
    'Duration (min)',
    'CPT Codes',
    'ICD Codes',
    'Check-In Location',
    'Check-Out Location',
    'Manual Override',
    'Status',
  ]

  const rows = claims.map(claim => [
    claim.id,
    claim.patient.name,
    claim.patient.dob,
    claim.provider.name,
    claim.serviceDate,
    claim.startTime,
    claim.endTime,
    claim.durationMinutes.toString(),
    claim.cpt.join('; '),
    claim.icd.join('; '),
    claim.evv.checkInLocation?.address || 'N/A',
    claim.evv.checkOutLocation?.address || 'N/A',
    claim.evv.manualOverride ? 'Yes' : 'No',
    claim.claimStatus,
  ])

  const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n')
  return csvContent
}

/**
 * Get audit trail
 */
export const getAuditTrail = async (
  entityId?: string
): Promise<AuditEntry[]> => {
  await delay()
  if (entityId) {
    return auditTrailStore.filter(entry => entry.entityId === entityId)
  }
  return [...auditTrailStore]
}

/**
 * Get current location (mock geolocation)
 */
export const getCurrentLocation = async (): Promise<{
  lat: number
  lng: number
  address: string
}> => {
  await delay(1000)

  if (!demoControls.geolocationEnabled) {
    throw new Error('Geolocation is disabled')
  }

  // Mock location (New Delhi, India)
  return {
    lat: 28.6139 + (Math.random() - 0.5) * 0.01,
    lng: 77.209 + (Math.random() - 0.5) * 0.01,
    address: '123 Therapy Center, Connaught Place, New Delhi, India',
  }
}

/**
 * Complete session and process payment
 * Handles both insurance and self-pay scenarios
 */
export const completeSessionPayment = async (
  sessionId: string
): Promise<Session> => {
  await delay(1500) // Longer delay to simulate payment processing
  if (shouldFail()) {
    throw new Error('Failed to complete session payment')
  }

  const contract = contractsStore.find(c =>
    c.sessions.some(s => s.id === sessionId)
  )
  if (!contract) {
    throw new Error('Session not found')
  }

  const session = contract.sessions.find(s => s.id === sessionId)
  if (!session) {
    throw new Error('Session not found')
  }

  // Validate session has been checked out
  if (!session.evv.checkOutAt) {
    throw new Error('Session must be checked out before completion')
  }

  // Validate session has finalized notes
  const hasFinalizedNotes = session.notes.some(n => n.status === 'finalized')
  if (!hasFinalizedNotes) {
    throw new Error('Session must have finalized notes before completion')
  }

  if (!session.paymentInfo) {
    throw new Error('Session payment information not found')
  }

  const now = new Date().toISOString()

  // Process payment based on payment type
  if (session.paymentInfo.paymentType === 'self-pay') {
    // Self-pay: Process full payment from pre-authorized Stripe account
    session.paymentInfo.paymentStatus = 'fully-paid'
    session.paymentInfo.invoiceId = `INV-${new Date().getFullYear()}-${Math.floor(Math.random() * 9000) + 1000}`
    session.paymentInfo.invoiceDate = now
    session.paymentInfo.paymentDate = now
    session.paymentInfo.patientOwedAmount = 0

    // Mark session as completed once payment is processed
    session.status = 'completed'

    addAuditEntry({
      action: 'PAYMENT_PROCESSED',
      user: contract.therapist.name,
      details: {
        sessionId,
        paymentType: 'self-pay',
        amount: session.paymentInfo.totalAmount,
        invoiceId: session.paymentInfo.invoiceId,
      },
      entityType: 'session',
      entityId: sessionId,
    })
  } else if (session.paymentInfo.paymentType === 'insurance') {
    // Insurance: Submit claim to Claim.MD
    session.status = 'completed'
    session.paymentInfo.claimStatus = 'pending_ack'
    session.paymentInfo.claimSubmittedDate = now
    // Update payment status to indicate claim processing has started
    session.paymentInfo.paymentStatus = 'in-progress'

    // Don't set insurance paid amount or co-pay yet
    // These will only be determined after insurance processes the claim
    // Set to 0 initially to indicate claim is being processed
    session.paymentInfo.insurancePaidAmount = 0
    session.paymentInfo.coPayAmount = 0
    session.paymentInfo.patientOwedAmount = 0

    // Session remains in 'clocked-out' status until claim is processed
    // It will be marked as 'completed' when claim is approved/rejected
    // For now, keep it as clocked-out to indicate waiting for insurance processing

    // Note: In a real scenario, the insurance company would process this asynchronously
    // and later update the insurancePaidAmount and coPayAmount based on their decision
    // Any remaining balance after insurance payment would be auto-deducted from patient's bank account

    addAuditEntry({
      action: 'CLAIM_SUBMITTED',
      user: contract.therapist.name,
      details: {
        sessionId,
        paymentType: 'insurance',
        totalAmount: session.paymentInfo.totalAmount,
        claimStatus: 'pending_ack',
      },
      entityType: 'session',
      entityId: sessionId,
    })
  }

  return { ...session }
}
